/**
 * JoinAcceptPage — /join/:token — Manager Invite accept landing (#75).
 *
 * Pre-auth surface. On load it resolves the invite token via
 * /api/manager-invite-resolve (no auth) and renders the matching state. The
 * Join button finalizes membership via /api/manager-invite-accept:
 *   - signed-in invitee -> accept directly with the Supabase access token
 *   - not signed in     -> send a one-time sign-in link to the invited
 *                          address; they return here and click Join again
 * Clicking Join is the Terms-of-Service assent — the clickwrap line sits
 * directly under the button (design lock decision 3, V1_Manager_Invite_
 * Flow_Design_Lock.md).
 *
 * Cross-tenant: V1 blocks an invitee who already belongs to another tenant
 * and routes them to a support path (per manager-invite-accept.js). The
 * mockup's "second workspace" variant is the deferred V2 tenant picker.
 *
 * Mockup: design/mockups/V1_Join_Accept_Page.html
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Wordmark } from "../components/index.js";
import { supabase } from "../lib/supabase.js";

const ROLE_LABEL = { manager: "Manager", tenant_admin: "Admin" };

const T = {
  navy: "var(--ppr-color-accent-navy, #1C2A40)",
  coral: "var(--ppr-color-accent-coral, #E36B4F)",
  coralDark: "var(--ppr-color-accent-coral-dark, #B85138)",
  textSecondary: "var(--ppr-color-text-secondary, #4B5563)",
  textMuted: "var(--ppr-color-text-muted, #94A3B8)",
  paleSoft: "var(--ppr-color-accent-pale-soft, #F4F8FB)",
  pale: "var(--ppr-color-accent-pale, #E8F1F8)",
  border: "var(--ppr-color-border-default, #E5E7EB)",
  hairline: "var(--ppr-color-border-strong, #D1D5DB)",
  gold: "var(--ppr-color-accent-gold-soft, #FBF6EC)",
  goldDeep: "var(--ppr-color-accent-gold-deep, #8A6926)",
  fontDisplay: "var(--ppr-typography-font-family-display, 'Archivo Black', sans-serif)",
  fontBody: "var(--ppr-typography-font-family-body, Lora, Georgia, serif)",
  fontUi: "var(--ppr-typography-font-family-ui, Inter, system-ui, sans-serif)",
  fontMono: "var(--ppr-typography-font-family-mono, 'JetBrains Mono', monospace)",
};

const pageStyle = {
  minHeight: "70vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "56px 24px",
  background: `linear-gradient(180deg, ${T.paleSoft} 0%, transparent 60%)`,
};
const cardStyle = {
  background: "#FFFFFF",
  border: `1px solid ${T.hairline}`,
  borderRadius: 10,
  maxWidth: 468,
  width: "100%",
  padding: "38px 38px 32px",
  textAlign: "center",
  boxShadow: "0 10px 34px rgba(28,42,64,0.10)",
};
const eyebrowStyle = {
  fontFamily: T.fontDisplay,
  fontSize: 11,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: T.coral,
  margin: "0 0 11px",
};
const h1Style = {
  fontFamily: T.fontDisplay,
  fontSize: 24,
  lineHeight: 1.16,
  color: T.navy,
  letterSpacing: "-0.02em",
  margin: "0 0 14px",
};
const bodyStyle = {
  fontFamily: T.fontBody,
  fontSize: 15,
  lineHeight: 1.6,
  color: T.textSecondary,
  margin: "0 0 18px",
};
const footStyle = {
  fontFamily: T.fontMono,
  fontSize: 11,
  color: T.textMuted,
  marginTop: 24,
};

export default function JoinAcceptPage() {
  const { token } = useParams();
  // resolving | pending | expired | revoked | invalid | accepted
  //   | check_email | joining | joined | cross_tenant | error | declined
  const [view, setView] = useState("resolving");
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [crossTenant, setCrossTenant] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setView("invalid");
        return;
      }
      try {
        const res = await fetch("/api/manager-invite-resolve", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ invite_token: token }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data.status) {
          setView("invalid");
          return;
        }
        if (data.status === "pending") {
          setInvite(data);
          setView("pending");
        } else {
          setView(data.status);
        }
      } catch {
        if (!cancelled) setView("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onJoin = useCallback(async () => {
    setErrorMsg("");
    const { data: { session } = {} } = await supabase.auth.getSession();
    if (!session?.access_token) {
      // No session yet — send a one-time sign-in link to the invited
      // address. emailRedirectTo brings them back to this same /join URL,
      // signed in, to click Join again.
      const { error } = await supabase.auth.signInWithOtp({
        email: invite?.invited_email,
        options: { emailRedirectTo: window.location.href },
      });
      if (error) {
        setErrorMsg(error.message || "Could not send the sign-in link. Try again.");
        setView("error");
        return;
      }
      setView("check_email");
      return;
    }
    setView("joining");
    try {
      const res = await fetch("/api/manager-invite-accept", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ invite_token: token, tos_accepted: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.accepted) {
        setView("joined");
        return;
      }
      if (data.code === "cross_tenant") {
        setCrossTenant(data);
        setView("cross_tenant");
        return;
      }
      if (data.code === "expired") {
        setView("expired");
        return;
      }
      if (typeof data.code === "string" && data.code.startsWith("status_revoked")) {
        setView("revoked");
        return;
      }
      setErrorMsg(data.error || "Could not finish joining. Try again.");
      setView("error");
    } catch {
      setErrorMsg("Could not reach the server. Try again.");
      setView("error");
    }
  }, [invite, token]);

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ marginBottom: 22 }}>
          <Wordmark scale={1.9} />
        </div>
        <Body
          view={view}
          invite={invite}
          errorMsg={errorMsg}
          crossTenant={crossTenant}
          onJoin={onJoin}
          onDecline={() => setView("declined")}
        />
      </div>
      <p style={footStyle}>An invite is good for 7 days after it's sent.</p>
    </div>
  );
}

function Body({ view, invite, errorMsg, crossTenant, onJoin, onDecline }) {
  const tenant = invite?.tenant_name || "the workspace";

  if (view === "resolving" || view === "joining") {
    return (
      <p style={bodyStyle}>
        {view === "joining" ? "Finishing up…" : "Checking your invitation…"}
      </p>
    );
  }

  if (view === "pending") {
    const role = ROLE_LABEL[invite?.invited_role] || "Manager";
    return (
      <>
        <p style={eyebrowStyle}>Team invite</p>
        <h1 style={h1Style}>Join {tenant} on Interaction Engineering.</h1>
        <p style={bodyStyle}>
          <strong style={{ color: T.navy }}>{invite?.inviter_name || "A teammate"}</strong>{" "}
          invited you to the {tenant} workspace. You'll get the full Blueprint
          library for the people you work with.
        </p>
        <RoleRow role={role} />
        <button type="button" style={joinBtnStyle} onClick={onJoin}>
          Join {tenant}
        </button>
        <p style={clickwrapStyle}>
          By joining, you agree to the Interaction Engineering{" "}
          <Link to="/terms" style={{ color: T.navy }}>Terms of Service</Link> and{" "}
          <Link to="/privacy" style={{ color: T.navy }}>Privacy Policy</Link>.
          <span style={clickwrapNewStyle}>
            New to Interaction Engineering? You'll set up your sign-in right after.
          </span>
        </p>
        <p style={declineStyle}>
          Not expecting this?{" "}
          <button type="button" style={linkBtnStyle} onClick={onDecline}>
            Decline the invite
          </button>
          .
        </p>
      </>
    );
  }

  if (view === "check_email") {
    return (
      <>
        <p style={eyebrowStyle}>Almost there</p>
        <h1 style={h1Style}>Check your email.</h1>
        <p style={bodyStyle}>
          We sent a one-time sign-in link to{" "}
          <strong style={{ color: T.navy }}>{invite?.invited_email}</strong>. Open it
          and you'll come right back here to finish joining {tenant}.
        </p>
      </>
    );
  }

  if (view === "joined") {
    return (
      <>
        <p style={eyebrowStyle}>Welcome</p>
        <h1 style={h1Style}>You're in.</h1>
        <p style={bodyStyle}>You've joined {tenant} on Interaction Engineering.</p>
        <Link to="/" style={joinBtnStyle}>Go to Interaction Engineering</Link>
      </>
    );
  }

  if (view === "accepted") {
    return (
      <>
        <p style={eyebrowStyle}>Already a member</p>
        <h1 style={h1Style}>You've already joined.</h1>
        <p style={bodyStyle}>This invitation was already accepted — you're all set.</p>
        <Link to="/" style={joinBtnStyle}>Go to Interaction Engineering</Link>
      </>
    );
  }

  if (view === "expired") {
    return (
      <EdgeCard
        glyph
        eyebrow="Invite expired"
        h1="This invite has expired."
        body="Invites are good for 7 days. Ask your workspace admin to send a new one — the original link no longer works."
      />
    );
  }

  if (view === "revoked") {
    return (
      <EdgeCard
        eyebrow="Invite no longer active"
        h1="This invite was withdrawn."
        body="An admin revoked this invitation. Ask them to send a new one if you still need access."
      />
    );
  }

  if (view === "invalid") {
    return (
      <EdgeCard
        eyebrow="Invalid link"
        h1="This invitation link isn't valid."
        body="The link may be incomplete or mistyped. Ask whoever invited you to send a fresh one."
      />
    );
  }

  if (view === "declined") {
    return (
      <>
        <p style={eyebrowStyle}>Invite declined</p>
        <h1 style={h1Style}>You've declined this invite.</h1>
        <p style={bodyStyle}>
          No problem — nothing was added to any account. You can close this page.
        </p>
      </>
    );
  }

  if (view === "cross_tenant") {
    return (
      <>
        <p style={eyebrowStyle}>Already on Interaction Engineering</p>
        <h1 style={h1Style}>This account is on another workspace.</h1>
        <div style={xtPanelStyle}>
          <p style={xtLabelStyle}>One account, one workspace — for now</p>
          <p style={xtBodyStyle}>
            {crossTenant?.support_message ||
              "This account already belongs to another organization on Interaction Engineering. Contact us and we'll help — or accept the invitation with a different email address."}
          </p>
        </div>
        <a
          href={`mailto:${crossTenant?.support_email || ""}`}
          style={joinBtnStyle}
        >
          Contact support
        </a>
      </>
    );
  }

  // view === "error"
  return (
    <>
      <p style={eyebrowStyle}>Something went wrong</p>
      <h1 style={h1Style}>We couldn't finish that.</h1>
      <p style={bodyStyle}>{errorMsg || "Please try again in a moment."}</p>
      <button type="button" style={joinBtnStyle} onClick={onJoin}>
        Try again
      </button>
    </>
  );
}

function RoleRow({ role }) {
  return (
    <div style={roleRowStyle}>
      Your role: <span style={roleChipStyle}>{role}</span>
    </div>
  );
}

function EdgeCard({ glyph = false, eyebrow, h1, body }) {
  return (
    <>
      {glyph && (
        <div style={{ fontFamily: T.fontDisplay, fontSize: 30, color: T.coralDark, marginBottom: 8 }}>
          &#9888;
        </div>
      )}
      <p style={{ ...eyebrowStyle, color: T.coralDark }}>{eyebrow}</p>
      <h1 style={h1Style}>{h1}</h1>
      <p style={bodyStyle}>{body}</p>
    </>
  );
}

const joinBtnStyle = {
  display: "block",
  width: "100%",
  padding: "14px 20px",
  background: T.coral,
  color: "#FFFFFF",
  border: `2px solid ${T.coral}`,
  borderRadius: 7,
  fontFamily: T.fontUi,
  fontWeight: 700,
  fontSize: 15,
  textDecoration: "none",
  cursor: "pointer",
  textAlign: "center",
  boxSizing: "border-box",
};
const clickwrapStyle = {
  fontFamily: T.fontBody,
  fontSize: 12.5,
  lineHeight: 1.55,
  color: T.textSecondary,
  margin: "13px 0 0",
};
const clickwrapNewStyle = {
  display: "block",
  marginTop: 4,
  color: T.textMuted,
  fontStyle: "italic",
  fontSize: 12,
};
const declineStyle = {
  fontSize: 12.5,
  fontFamily: T.fontUi,
  color: T.textMuted,
  margin: "22px 0 0",
  paddingTop: 18,
  borderTop: `1px solid ${T.border}`,
};
const linkBtnStyle = {
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  color: "var(--ppr-color-accent-steel, #3F5C7C)",
  textDecoration: "underline",
  cursor: "pointer",
};
const roleRowStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: T.paleSoft,
  border: `1px solid ${T.border}`,
  borderRadius: 999,
  padding: "6px 14px",
  marginBottom: 22,
  fontSize: 12.5,
  fontFamily: T.fontUi,
  color: T.textSecondary,
};
const roleChipStyle = {
  display: "inline-block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.03em",
  padding: "3px 10px",
  borderRadius: 999,
  background: T.pale,
  color: T.navy,
};
const xtPanelStyle = {
  background: T.gold,
  border: "1px solid #E8D7B0",
  borderRadius: 7,
  padding: "14px 16px",
  margin: "0 0 20px",
  textAlign: "left",
};
const xtLabelStyle = {
  fontSize: 10.5,
  fontFamily: T.fontUi,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: T.goldDeep,
  margin: "0 0 5px",
};
const xtBodyStyle = {
  fontFamily: T.fontBody,
  fontSize: 13,
  lineHeight: 1.55,
  color: T.navy,
  margin: 0,
};
